// ============================================================
// TMR Portal Admin — o que mudou entre duas versões de um modelo, palavra a palavra
// ============================================================
import { useState } from 'react';
import { diffItems } from '../../data/contractTemplates/diff';
import type { DiffLine, TemplateDiff } from '../../data/contractTemplates/contract';

/** The colours of what went out and what came in — a line tinted, the words inside it marked harder. */
const TONE = {
  Removed: { line: 'oklch(97% 0.02 28)', word: 'oklch(90% 0.07 28)', text: 'oklch(38% 0.12 28)', sign: '−' },
  Inserted: { line: 'oklch(97% 0.02 155)', word: 'oklch(89% 0.07 155)', text: 'oklch(34% 0.08 155)', sign: '+' },
} as const;

/** Props for {@link TemplateDiffView}. */
interface TemplateDiffViewProps {
  diff: TemplateDiff;
  /** Caps the height, scrolling inside — for a dialog, where the comparison shares room with a form. */
  maxHeight?: number;
}

/**
 * What changed between two versions of a template.
 *
 * **Word by word, not line by line.** In Markdown a paragraph is one line, and a contract's paragraphs are
 * long: a whole-line diff of a clause where one number changed shows two near-identical paragraphs and
 * leaves the reader to find the difference — which is exactly the reading this view exists to spare. The
 * backend already marks the words that changed inside an altered line; they are shown here.
 *
 * Whitespace is shown rather than hidden: in Markdown a trailing space changes the rendered result, and any
 * change at all changes the version's hash — a diff that tidied it away would show two versions as
 * identical while the engine treats them as different documents.
 *
 * Unchanged stretches are folded around the changes, and each fold opens on a click — the lines next to a
 * change say which clause it is in, the rest of the contract says nothing about it.
 */
export function TemplateDiffView({ diff, maxHeight }: TemplateDiffViewProps) {
  const [unfolded, setUnfolded] = useState<ReadonlySet<number>>(new Set());

  if (diff.identical) {
    return (
      <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
        Nenhuma diferença: o texto das versões {diff.fromVersionNumber} e {diff.toVersionNumber} é idêntico.
      </span>
    );
  }

  const items = diffItems(diff.lines);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
        Da versão {diff.fromVersionNumber} para a {diff.toVersionNumber}: {countLabel(diff.inserted, 'incluída', 'incluídas')}
        {' e '}
        {countLabel(diff.removed, 'removida', 'removidas')}.
      </span>

      <div
        style={{
          maxHeight,
          overflowY: maxHeight ? 'auto' : undefined,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-mono)',
          fontSize: 12.5,
          lineHeight: 1.6,
        }}
      >
        {items.map((item, index) => {
          if (item.kind === 'line') return <Line key={index} line={item.line} />;

          if (unfolded.has(index)) {
            return item.lines.map((line, offset) => <Line key={`${index}-${offset}`} line={line} />);
          }

          return (
            <button
              key={index}
              type="button"
              onClick={() => setUnfolded((open) => new Set(open).add(index))}
              style={{
                display: 'block',
                width: '100%',
                padding: '3px 10px',
                border: 0,
                borderTop: '1px dashed var(--border-default)',
                borderBottom: '1px dashed var(--border-default)',
                background: 'var(--cream-100)',
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {item.lines.length === 1 ? '1 linha igual' : `${item.lines.length} linhas iguais`} · mostrar
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** One line of the comparison, with its numbers on either side and the changed words marked. */
function Line({ line }: { line: DiffLine }) {
  const tone = line.kind === 'Unchanged' ? null : TONE[line.kind];

  return (
    <div style={{ display: 'flex', background: tone?.line, color: tone?.text }}>
      <span style={gutter}>{line.numberBefore ?? ''}</span>
      <span style={gutter}>{line.numberAfter ?? ''}</span>
      <span style={{ width: 16, flexShrink: 0, textAlign: 'center', userSelect: 'none' }}>{tone?.sign ?? ''}</span>
      <span style={{ flex: 1, padding: '1px 10px 1px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {line.fragments
          ? line.fragments.map((fragment, index) =>
              fragment.kind === 'Unchanged' || !tone ? (
                <span key={index}>{fragment.text}</span>
              ) : (
                <span
                  key={index}
                  style={{
                    background: tone.word,
                    borderRadius: 2,
                    textDecoration: fragment.kind === 'Removed' ? 'line-through' : undefined,
                  }}
                >
                  {fragment.text}
                </span>
              ),
            )
          : line.text || ' '}
      </span>
    </div>
  );
}

const gutter = {
  width: 36,
  flexShrink: 0,
  padding: '1px 6px',
  textAlign: 'right',
  color: 'var(--fg-subtle)',
  userSelect: 'none',
  fontSize: 11.5,
} as const;

/** "1 linha incluída", "3 linhas removidas", "nenhuma linha removida". */
function countLabel(count: number, singular: string, plural: string): string {
  if (count === 0) return `nenhuma linha ${singular}`;
  if (count === 1) return `1 linha ${singular}`;

  return `${count} linhas ${plural}`;
}
